import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

// Returns true if the stored token is for a SaaS user (SUPER_ADMIN / DISTRIBUTOR / CLIENT)
const isSaasToken = (token) => {
  if (!token) return false;
  try {
    // JWT payload is the second segment, base64-decoded
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.type === 'SAAS';
  } catch {
    return false;
  }
};

const setAuthHeader = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// AuthProvider
// ─────────────────────────────────────────────────────────────────────────────
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set up axios response interceptor
  useEffect(() => {
    const token = localStorage.getItem('token');
    setAuthHeader(token);

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        const isLoginRequest = error.config?.url && error.config.url.includes('/auth/login');
        if (error.response?.status === 401 && !isLoginRequest) {
          logout();
          toast.error('Session expired. Please login again.');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  // ── checkAuth ──────────────────────────────────────────────────────────────
  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      setAuthHeader(token);

      if (isSaasToken(token)) {
        // SaaS user — fetch profile from SaaS endpoint
        const res = await api.get('/api/saas/auth/profile');
        if (res.data.success) {
          setUser(res.data.data);
        } else {
          localStorage.removeItem('token');
          setAuthHeader(null);
        }
      } else {
        // Shop user (ADMIN / EMPLOYEE)
        const res = await api.get('/api/auth/profile');
        if (res.data.success) {
          setUser(res.data.data);
        } else {
          localStorage.removeItem('token');
          setAuthHeader(null);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setAuthHeader(null);
    } finally {
      setLoading(false);
    }
  };

  // ── login ──────────────────────────────────────────────────────────────────
  const login = async (credentials) => {
    try {
      // 1. Try SaaS login first (SUPER_ADMIN / DISTRIBUTOR / CLIENT)
      try {
        const saasRes = await api.post('/api/saas/auth/login', credentials);
        if (saasRes.data.success) {
          const { user: saasUser, token } = saasRes.data.data;
          localStorage.setItem('token', token);
          setAuthHeader(token);
          setUser(saasUser);
          toast.success('SaaS Login successful!');
          return { success: true };
        }
      } catch (saasErr) {
        // If account is inactive/suspended (403), show the specific error and stop.
        // Otherwise (401, etc.), fall through to the regular shop login.
        if (saasErr.response?.status === 403) {
          const msg = saasErr.response?.data?.message || 'Account suspended';
          toast.error(msg);
          return { success: false, message: msg };
        }
      }

      // 2. Try regular shop login (ADMIN / EMPLOYEE)
      const shopRes = await api.post('/api/auth/login', credentials);
      if (shopRes.data.success) {
        const { user: shopUser, token } = shopRes.data.data;
        localStorage.setItem('token', token);
        setAuthHeader(token);
        setUser(shopUser);
        toast.success('Login successful!');
        return { success: true };
      }

      toast.error(shopRes.data.message || 'Login failed');
      return { success: false, message: shopRes.data.message };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  // ── logout ─────────────────────────────────────────────────────────────────
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('selectedBranchId');
    setAuthHeader(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  // ── mockPaymentUnlock (subscription renewal for expired CLIENT) ────────────
  // Called from the ExpiredLockoutScreen after simulated payment
  const mockPaymentUnlock = async () => {
    if (user && user.role === 'CLIENT') {
      try {
        const res = await api.post(`/api/saas/clients/${user.clientId}/renew`);
        if (res.data.success) {
          toast.success('Payment authorized! Store successfully unlocked!');
          await checkAuth(); // Re-fetch fresh profile
        } else {
          toast.error(res.data.message || 'Renewal failed');
        }
      } catch (error) {
        console.error('Renewal error:', error);
        toast.error(error.response?.data?.message || 'Payment failed');
      }
    }
  };

  // ── updateProfile / changePassword (shop users only) ──────────────────────
  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/api/auth/profile', profileData);
      if (response.data.success) {
        setUser(response.data.data);
        toast.success('Profile updated successfully');
        return { success: true };
      }
      toast.error(response.data.message || 'Update failed');
      return { success: false, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.message || 'Update failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      const response = await api.put('/api/auth/change-password', passwordData);
      if (response.data.success) {
        toast.success('Password changed successfully');
        return { success: true };
      }
      toast.error(response.data.message || 'Password change failed');
      return { success: false, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const currencySymbol = user?.currencySymbol || '₹';
  const currencyCode = user?.currencyCode || 'INR';

  const value = {
    user,
    loading,
    login,
    logout,
    updateProfile,
    changePassword,
    checkAuth,
    mockPaymentUnlock,
    currencySymbol,
    currencyCode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
